import {useEffect, useState} from 'react';
import {Link} from 'react-router';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {CardGiftcardIcon} from '@ui/icons/material/CardGiftcard';
import {LocalOfferIcon} from '@ui/icons/material/LocalOffer';
import {money} from '@app/student/portal/portal-format';

/**
 * The partner offer a visitor is entitled to — from Settings → Referrals via
 * `referral/offer`. The server decides; this only shows it.
 *
 * Used for the welcome popup when somebody opens a partner link, and for the
 * "before / after" price labels on services and at checkout. The price a
 * student pays is still computed by the cart on the server.
 */
export interface ReferralOffer {
  partner_name: string | null;
  code: string;
  discount_available: boolean;
  discount_type: 'percent' | 'fixed';
  discount_value: number;
  discount_max: number;
  min_order: number;
  first_order_only: boolean;
  signed_in: boolean;
}

export const REFERRAL_OFFER_KEY = ['referral-offer'];
export const REFERRAL_WELCOME_EVENT = 'referral-welcome';

export function useReferralOffer(): ReferralOffer | null {
  const {data} = useQuery({
    queryKey: REFERRAL_OFFER_KEY,
    queryFn: () =>
      apiClient.get<{offer: ReferralOffer | null}>('referral/offer').then(r => r.data),
    staleTime: 5 * 60_000,
  });
  return data?.offer ?? null;
}

/** "10%" or "$5". */
export function offerLabel(offer: ReferralOffer, currency = 'USD'): string {
  const v = offer.discount_value;
  return offer.discount_type === 'fixed'
    ? money(v, currency)
    : `${Number.isInteger(v) ? v : v.toFixed(2)}%`;
}

/**
 * The price after the partner discount, in cents, or null when this price
 * would not get it (discount off, or below the minimum order).
 */
export function priceAfterOffer(cents: number, offer: ReferralOffer | null): number | null {
  if (!offer?.discount_available || !(cents > 0)) return null;
  if (offer.min_order > 0 && cents < Math.round(offer.min_order * 100)) return null;

  let off =
    offer.discount_type === 'fixed'
      ? Math.round(offer.discount_value * 100)
      : Math.round((cents * Math.min(100, offer.discount_value)) / 100);
  if (offer.discount_max > 0) off = Math.min(off, Math.round(offer.discount_max * 100));
  off = Math.max(0, Math.min(off, cents));

  return off > 0 ? cents - off : null;
}

/**
 * A price with the partner discount shown as before → after, plus a small
 * label. Renders the plain price when no discount applies.
 */
export function OfferPrice({
  cents,
  currency,
  className = 'text-lg',
  showBadge = true,
}: {
  cents: number;
  currency?: string | null;
  className?: string;
  showBadge?: boolean;
}) {
  const offer = useReferralOffer();
  const after = priceAfterOffer(cents, offer);
  const cur = currency || 'USD';

  if (after === null || !offer) {
    return (
      <span className={`font-bold text-primary ${className}`}>
        <bdi dir="ltr">{money(cents / 100, cur)}</bdi>
      </span>
    );
  }

  return (
    <span className="inline-flex flex-col items-start gap-2">
      <span className="inline-flex flex-wrap items-baseline gap-x-8">
        <span className={`font-bold text-positive ${className}`}>
          <bdi dir="ltr">{money(after / 100, cur)}</bdi>
        </span>
        <span className="text-sm font-medium text-muted line-through">
          <bdi dir="ltr">{money(cents / 100, cur)}</bdi>
        </span>
      </span>
      {showBadge && (
        <span className="inline-flex items-center gap-4 rounded-full bg-positive/10 px-8 py-2 text-2xs font-bold text-positive">
          <LocalOfferIcon size="xs" />
          <Trans message="Partner discount :value" values={{value: offerLabel(offer, cur)}} />
        </span>
      )}
    </span>
  );
}

/**
 * The popup shown once when somebody opens a partner link.
 *
 * Opened by an event from `useReferralCapture` after the server has recorded
 * the click and returned the offer, so it never promises something the server
 * did not confirm. Shown once per code per browser.
 */
export function ReferralWelcomeDialog() {
  const [offer, setOffer] = useState<ReferralOffer | null>(null);

  useEffect(() => {
    const onWelcome = (e: Event) => {
      const next = (e as CustomEvent<ReferralOffer>).detail;
      if (!next) return;
      const key = `partner-welcome-shown:${String(next.code).toLowerCase()}`;
      try {
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, '1');
      } catch {
        /* storage blocked — still show it this once */
      }
      setOffer(next);
    };
    window.addEventListener(REFERRAL_WELCOME_EVENT, onWelcome);
    return () => window.removeEventListener(REFERRAL_WELCOME_EVENT, onWelcome);
  }, []);

  return (
    <DialogTrigger
      type="modal"
      isOpen={offer !== null}
      onOpenChange={open => {
        if (!open) setOffer(null);
      }}
    >
      {offer && <WelcomeContent offer={offer} />}
    </DialogTrigger>
  );
}

function WelcomeContent({offer}: {offer: ReferralOffer}) {
  const {close} = useDialogContext();
  const label = offerLabel(offer);

  return (
    <Dialog size="sm">
      <DialogBody>
        <div className="px-4 pb-4 pt-12 text-center">
          <span className="mx-auto flex h-64 w-64 items-center justify-center rounded-full bg-positive/10 text-positive">
            <CardGiftcardIcon size="lg" />
          </span>

          <p className="mt-14 text-sm text-muted">
            {offer.partner_name ? (
              <Trans
                message="You were invited by :partner"
                values={{partner: <bdi className="font-semibold text-main">{offer.partner_name}</bdi>}}
              />
            ) : (
              <Trans message="You were invited by a Masertak partner" />
            )}
          </p>

          {offer.discount_available ? (
            <>
              <h2 className="mt-8 text-2xl font-bold leading-snug">
                <Trans
                  message={offer.first_order_only ? 'Get :value off your first order' : 'Get :value off your order'}
                  values={{value: <bdi dir="ltr" className="text-positive">{label}</bdi>}}
                />
              </h2>
              <p className="mt-8 text-sm leading-relaxed text-muted">
                {offer.min_order > 0 ? (
                  <Trans
                    message="The discount is applied automatically at checkout on orders of :min or more — no code needed."
                    values={{min: money(offer.min_order, 'USD')}}
                  />
                ) : (
                  <Trans message="The discount is applied automatically at checkout — no code needed." />
                )}
              </p>
            </>
          ) : (
            <h2 className="mt-8 text-xl font-bold leading-snug">
              <Trans message="Welcome to Masertak" />
            </h2>
          )}

          <div className="mt-20 flex flex-col gap-8">
            <Button
              variant="flat"
              color="primary"
              elementType={Link}
              to="/services"
              onClick={() => close()}
            >
              <Trans message="Browse services" />
            </Button>
            <Button variant="text" onClick={() => close()}>
              <Trans message="Maybe later" />
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}
